import { useState } from "react";
import { toast } from "sonner";
import { Trash2, ListChecks, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/campus";
import { MediaUploader } from "@/components/campus/MediaUploader";

const field = "w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500";

export const LessonEditor = ({ lesson, index, label = "Lesson", onChange, onRemove, prompts }) => {
  const [quizBusy, setQuizBusy] = useState(false);
  const set = (patch) => onChange({ ...lesson, ...patch });
  const tid = `lesson-${index}`;

  const makeQuiz = async () => {
    setQuizBusy(true);
    try {
      const { data } = await api.post("/ai/lesson-quiz", { title: lesson.title, body: lesson.body });
      set({ quiz: data.quiz });
      toast.success(`${data.quiz.length} quiz questions added`);
    } catch (err) {
      toast.error(apiError(err, "Couldn't make a quiz"));
    } finally {
      setQuizBusy(false);
    }
  };

  return (
    <div className="rounded-xl bg-[#121721] border border-white/10 p-4 space-y-3" data-testid={tid}>
      <div className="flex items-center gap-3">
        <span className="text-xs font-mono text-amber-400 shrink-0">{label} {index + 1}</span>
        <input data-testid={`${tid}-title`} value={lesson.title} placeholder="Lesson title" onChange={(e) => set({ title: e.target.value })} className={field} />
        <input data-testid={`${tid}-duration`} value={lesson.duration} placeholder="10 min" onChange={(e) => set({ duration: e.target.value })} className={`${field} w-24 shrink-0`} />
        {onRemove && (
          <button type="button" data-testid={`${tid}-remove`} onClick={onRemove} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 className="h-4 w-4" /></button>
        )}
      </div>
      {prompts?.length > 0 && (
        <ul className="text-xs text-slate-500 list-disc pl-5 space-y-0.5">{prompts.map((p) => <li key={p}>{p}</li>)}</ul>
      )}
      <textarea data-testid={`${tid}-body`} rows={4} value={lesson.body} onChange={(e) => set({ body: e.target.value })}
        placeholder="Your notes, in your own words — what students learn and do. (Shown alongside any video/audio/slides.)" className={field} />
      <MediaUploader media={lesson.media} onChange={(media) => set({ media })} testid={`${tid}-media`} />
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500" data-testid={`${tid}-quiz-count`}>{lesson.quiz?.length ? `${lesson.quiz.length} quiz questions` : "No quiz yet"}</span>
        <button type="button" data-testid={`${tid}-quiz-btn`} onClick={makeQuiz} disabled={quizBusy}
          className="flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 disabled:opacity-50">
          {quizBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5" />} AI quiz from my notes
        </button>
      </div>
    </div>
  );
};
